import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { GlobalSearch } from "@/components/GlobalSearch";
import { InstallDrelik } from "@/components/InstallDrelik";
import { AgreementGate } from "@/components/AgreementGate";
import api from "@/lib/api";
import {
  LayoutDashboard, Package, Boxes, Users, ShoppingCart, Settings,
  LogOut, Menu, X, ExternalLink, Store, Tag, BarChart3, Search,
} from "lucide-react";

const nav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/dashboard/products", label: "Products", icon: Package },
  { to: "/dashboard/inventory", label: "Inventory", icon: Boxes },
  { to: "/dashboard/price-sheets", label: "Price Sheets", icon: Tag },
  { to: "/dashboard/customers", label: "Customers", icon: Users },
  { to: "/dashboard/orders", label: "Orders", icon: ShoppingCart },
  { to: "/dashboard/reports", label: "Reports", icon: BarChart3 },
  { to: "/dashboard/settings", label: "Settings", icon: Settings },
];

export default function DashboardLayout() {
  const { user, storefront, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [legalGate, setLegalGate] = useState(null);

  useEffect(() => {
    api.get("/legal/acceptance-status").then((r) => setLegalGate(r.data.needs_acceptance)).catch(() => setLegalGate(false));
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const doLogout = async () => { await logout(); navigate("/login"); };

  if (legalGate === true) return <AgreementGate onAccepted={() => setLegalGate(false)} />;

  const SidebarInner = (
    <div className="flex flex-col h-full">
      <div className="h-16 flex items-center px-6 border-b border-border">
        <Logo />
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {nav.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150 ${
                isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
              }`
            }
            data-testid={`sidebar-${n.label.toLowerCase()}`}
          >
            <n.icon className="h-[18px] w-[18px]" strokeWidth={2} /> {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-border space-y-1">
        {storefront && (
          <a href={`/store/${storefront.slug}`} target="_blank" rel="noreferrer"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
            data-testid="view-storefront-link">
            <ExternalLink className="h-[18px] w-[18px]" /> View storefront
          </a>
        )}
        <div className="px-1 pb-1"><InstallDrelik variant="ghost" className="w-full justify-start !px-2 text-muted-foreground hover:text-foreground" /></div>
        <div className="flex items-center gap-3 px-3 py-2">
          <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm">
            {user?.name?.[0]?.toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user?.name}</p>
            <p className="text-xs text-muted-foreground truncate">{storefront?.name}</p>
          </div>
          <button onClick={doLogout} className="text-muted-foreground hover:text-destructive transition-colors p-1" data-testid="logout-btn" title="Sign out">
            <LogOut className="h-[18px] w-[18px]" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 bg-card border-r border-border flex-col z-30">
        {SidebarInner}
      </aside>

      {open && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 bg-card border-r border-border">
            <button className="absolute top-4 right-4 text-muted-foreground" onClick={() => setOpen(false)}><X className="h-5 w-5" /></button>
            {SidebarInner}
          </aside>
        </div>
      )}

      <div className="lg:pl-64">
        <header className="h-16 flex items-center justify-between px-4 border-b border-border bg-card sticky top-0 z-20 lg:px-8">
          <button onClick={() => setOpen(true)} className="lg:hidden" data-testid="mobile-menu-btn"><Menu className="h-6 w-6" /></button>
          <button onClick={() => setSearchOpen(true)}
            className="hidden lg:flex items-center gap-2 text-sm text-muted-foreground border border-border rounded-lg px-3 py-2 w-72 hover:bg-secondary transition-colors" data-testid="open-search-btn">
            <Search className="h-4 w-4" /> Search...
            <span className="ml-auto text-xs bg-secondary rounded px-1.5 py-0.5">⌘K</span>
          </button>
          <div className="lg:hidden"><Logo /></div>
          <button onClick={() => setSearchOpen(true)} className="lg:hidden p-2" data-testid="mobile-search-btn"><Search className="h-5 w-5" /></button>
        </header>
        <main className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
          <Outlet />
        </main>
      </div>
      <GlobalSearch open={searchOpen} setOpen={setSearchOpen} />
    </div>
  );
}
